import { useState } from 'react';
import { Button, Checkbox, Form, Input, Result, Spin, notification } from 'antd';
import { MaskedInput } from 'antd-mask-input';
import { Link, useNavigate } from 'react-router-dom';
import { DiContainer } from '@/di/di-container';
import { ClientStorageService } from '@/common/services/client-storage.service';
import { useGetShoppingCartDetailsQuery } from '@/shoppingCart/services/shopping-cart.service';
import { useMarkBunkerAsConfiguredMutation } from '@/services/picker.service';
import {
  ICreateLeadPayload,
  useCreateLeadMutation,
} from '@/checkout/services/lead.service';
import { generateSpecPdf } from '@/checkout/helpers/generate-spec-pdf';
import { useGetSettingsQuery } from '@/services/settings.service';
import { addSpacesInNumbers } from '@/configurator/helpers/add-spaces-in-numbers';

const clientStorageService = DiContainer.get(ClientStorageService);
const emailRegexp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CheckoutView = () => {
  const navigate = useNavigate();
  const session = clientStorageService.getUUID();
  const [form] = Form.useForm();

  const {
    data: cart,
    isLoading: cartLoading,
    isFetching: cartFetching,
  } = useGetShoppingCartDetailsQuery(
    {
      session,
      sharedId: null,
    },
    { refetchOnMountOrArgChange: true }
  );
  const loading = cartLoading || cartFetching;
  const { data: settings } = useGetSettingsQuery();
  const showPrices = !!settings?.show_prices;
  const [createLead, { isLoading: submitting }] = useCreateLeadMutation();
  const [markBunkerAsConfigured] = useMarkBunkerAsConfiguredMutation();

  const [contact, setContact] = useState<ICreateLeadPayload | null>(null);
  const [done, setDone] = useState(false);
  const [goingBack, setGoingBack] = useState(false);

  // «Назад» в Эксперт: снимаем флаг configured с позиций корзины, чтобы они
  // снова появились в групповом Эксперте (выбор опций сохранён в шагах).
  const goBackToExpert = async () => {
    setGoingBack(true);
    try {
      if (cart) {
        await Promise.all(
          cart.modifications.map((g) =>
            markBunkerAsConfigured({
              id: g.picker_id,
              configured: false,
              session,
            }).unwrap()
          )
        );
      }
    } catch {
      /* даже при ошибке ведём в Эксперт */
    } finally {
      setGoingBack(false);
      navigate('/options');
    }
  };

  const isEmpty = !loading && (!cart || !cart.modifications.length);

  const onFinish = async (values: ICreateLeadPayload) => {
    const payload: ICreateLeadPayload = {
      full_name: values.full_name,
      phone: values.phone,
      email: values.email,
      organization: values.organization,
      comment: values.comment,
    };
    const res = await createLead({ session, lead: payload });
    if ('data' in res) {
      setContact(payload);
      setDone(true);
    } else {
      notification.error({ message: 'Не удалось отправить заявку. Попробуйте ещё раз.' });
    }
  };

  if (done && cart && contact) {
    return (
      <div className="container py-10">
        <Result
          status="success"
          title="Заявка отправлена"
          subTitle="Менеджер свяжется с вами для уточнения деталей и стоимости. Вы можете скачать спецификацию конфигурации."
          extra={[
            <Button
              key="pdf"
              type="primary"
              size="large"
              style={{ background: '#66B28A' }}
              onClick={() => generateSpecPdf(cart, contact, showPrices)}>
              Скачать PDF спецификации
            </Button>,
            <Button key="home" size="large" onClick={() => navigate('/configurator')}>
              В конфигуратор
            </Button>,
          ]}
        />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="container py-10">
        <Result
          status="info"
          title="Корзина пуста"
          subTitle="Сначала соберите конфигурацию техники."
          extra={
            <Button type="primary" onClick={() => navigate('/configurator')}>
              К конфигуратору
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="container max-w-[1100px] py-8">
      <Button
        className="mb-4"
        loading={goingBack}
        onClick={goBackToExpert}>
        ← Назад к настройке опций
      </Button>
      <h2 className="text-[24px] font-semibold mb-6">Оформление заявки</h2>

      <Spin spinning={loading}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-[18px] font-semibold mb-4">Контактные данные</h3>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Form.Item
                name="full_name"
                label="ФИО"
                rules={[{ required: true, message: 'Укажите ФИО' }]}>
                <Input className="h-[48px]" placeholder="Иванов Иван Иванович" />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Контактный телефон"
                rules={[
                  {
                    required: true,
                    message: 'Укажите телефон',
                    min: 11,
                    transform: (v: string) => (v || '').replace(/\D/g, ''),
                  },
                ]}>
                <MaskedInput mask={'+7(000)000-00-00'} className="h-[48px]" />
              </Form.Item>
              <Form.Item
                name="email"
                label="E-mail"
                rules={[{ pattern: emailRegexp, message: 'Некорректный e-mail' }]}>
                <Input className="h-[48px]" placeholder="email@example.ru" />
              </Form.Item>
              <Form.Item name="organization" label="Организация">
                <Input className="h-[48px]" placeholder="ООО «...»" />
              </Form.Item>
              <Form.Item name="comment" label="Комментарий">
                <Input.TextArea rows={3} placeholder="Пожелания, сроки и т.п." />
              </Form.Item>
              <Form.Item
                name="consent"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, v) =>
                      v
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error('Необходимо согласие на обработку данных')
                          ),
                  },
                ]}>
                <Checkbox>
                  Я согласен на обработку персональных данных в соответствии с{' '}
                  <Link to="/privacy" target="_blank" className="text-[#007E3C] underline">
                    политикой
                  </Link>
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={submitting}
                  style={{ background: '#66B28A', height: 52 }}>
                  Отправить заявку
                </Button>
              </Form.Item>
            </Form>
          </div>

          <div>
            <h3 className="text-[18px] font-semibold mb-4">Состав конфигурации</h3>
            <div className="flex flex-col gap-4">
              {cart?.modifications.map((group) =>
                group.items.map((item) => {
                  const opts = Array.from(
                    new Set(
                      [
                        ...(item.extra_options?.steps || []),
                        ...(item.options?.steps || []),
                      ]
                        .map((s) => s?.name)
                        .filter(Boolean)
                    )
                  );
                  return (
                    <div
                      key={item.id}
                      className="rounded-lg border border-[#E5E7EB] p-4">
                      <div className="font-semibold">{item.name}</div>
                      <div className="text-[#555]">{item.modification}</div>
                      {showPrices && (
                        <div className="mt-1 font-semibold text-[#007E3C]">
                          {addSpacesInNumbers(Math.round(item.amount))} ₽
                        </div>
                      )}
                      {opts.length > 0 && (
                        <ul className="mt-2 list-disc pl-5 text-[14px] text-[#555]">
                          {opts.map((o) => (
                            <li key={o}>{o}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {showPrices ? (
              <>
                <div className="mt-4 flex justify-between border-t pt-3 text-[18px] font-semibold">
                  <span>Итого:</span>
                  <span className="text-[#007E3C]">
                    {addSpacesInNumbers(Math.round(cart?.total_amount || 0))} ₽
                  </span>
                </div>
                <p className="mt-1 text-[12px] text-[#999]">
                  Цены ориентировочные, без НДС. Окончательную стоимость
                  подтверждает менеджер.
                </p>
              </>
            ) : (
              <p className="mt-4 text-[12px] text-[#999]">
                Цены не отображаются — окончательную стоимость рассчитает
                менеджер.
              </p>
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};
